import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link, useLocation, type To } from 'react-router-dom';

import { CompactMenu, CondensedTip, useCondensed, type ICompactChip } from '@/design';
import type { IAdminTab } from '../../../components/admin-page';
import { FunnelChoice, FunnelHeader, ParamFunnel } from '../../compliance/queue-headers';
import { ParamChip } from '../../sec-adv/firm-filters';

/**
 * The parts the light work pages share (the work pages design, "Three forms": Import, the
 * BrokerCheck refresh's runs, Reports, the ranking editions) — over the kit's own
 * headers and chips, for a list whose route takes a filter of a known vocabulary, one value each,
 * with no facets read: the funnel offers the vocabulary, and the value an old address names that
 * the vocabulary lacks, as spelled.
 */

/** One single-valued filter of a known vocabulary: its URL key, the column it heads, its words. */
export interface IKnownFilter {
  param: string;
  column: string;
  label: string;
  options: readonly { value: string; label: string }[];
  /** A value's words — the vocabulary's, else as spelled. */
  labelOf: (value: string) => string;
  /** The empty value is a value (`?cycle=`, the editions on no cycle), not "Any". */
  emptyIsValue?: boolean;
  /** The column's header, when it is not the filter's label. */
  heading?: string;
}

/** The funnels' open one, held by the page so a filter chip's press opens its column's. */
export function useKnownFunnels(
  filters: readonly IKnownFilter[],
  params: URLSearchParams,
  choose: (param: string, value: Nullable<string>) => void
) {
  const [open, setOpen] = React.useState<Nullable<string>>(null);
  const valueOf = (filter: IKnownFilter) => {
    const value = params.get(filter.param);
    return value === '' && !filter.emptyIsValue ? null : value;
  };
  const body = (filter: IKnownFilter) => {
    const value = valueOf(filter);
    const unknown = value !== null && !filter.options.some((option) => option.value === value);
    return (
      <FunnelChoice
        label={filter.label}
        value={value}
        options={[
          { value: null, label: 'Any' },
          ...filter.options,
          ...(unknown ? [{ value, label: filter.labelOf(value) }] : [])
        ]}
        onChoose={(next) => {
          choose(filter.param, next);
          setOpen(null);
        }}
      />
    );
  };
  /** The column's header: its label and its funnel. */
  const header = (filter: IKnownFilter) => {
    const value = valueOf(filter);
    return (
      <FunnelHeader
        label={filter.heading ?? filter.label}
        funnel={
          <ParamFunnel
            code={filter.column}
            label={filter.label}
            said={value === null ? null : filter.labelOf(value)}
            open={open === filter.column}
            onOpenChange={(next) => setOpen(next ? filter.column : null)}
          >
            {body(filter)}
          </ParamFunnel>
        }
      />
    );
  };
  /** The filters set, as the filter line's chips — a press opens the column's funnel, ✕ drops it. */
  const chips: ICompactChip[] = filters.flatMap((filter) => {
    const value = valueOf(filter);
    if (value === null) return [];
    return [
      {
        key: filter.param,
        chip: (
          <ParamChip
            words={`${filter.label}: ${filter.labelOf(value)}`}
            body={body(filter)}
            onPress={() => {
              const drawn = document.querySelector(`[data-funnel="${CSS.escape(filter.column)}"]`);
              if (drawn === null) return false;
              setOpen(filter.column);
              return true;
            }}
            onRemove={() => choose(filter.param, null)}
          />
        )
      }
    ];
  });
  return { header, chips };
}

/** A page's routed tabs in its compact bar: the open one, the others a menu away. */
export function CompactTabs({ label, tabs }: { label: string; tabs: readonly IAdminTab[] }) {
  const { pathname } = useLocation();
  const current = (tab: IAdminTab) => (tab.end ? pathname === tab.to : pathname.startsWith(tab.to));
  const open = tabs.find(current);
  return (
    <CompactMenu
      label={label}
      trigger={open?.label ?? label}
      items={tabs.map((tab) => ({
        key: tab.to,
        label: tab.label,
        to: { pathname: tab.to },
        current: tab === open
      }))}
    />
  );
}

/** The page's own action — a link or a press — its icon and words, slimmed to the icon (named the
 *  same) in a compact bar. */
export function PageAction({
  label,
  icon,
  to,
  state,
  onClick,
  disabled,
  describedBy
}: {
  label: string;
  icon: React.ReactNode;
  to?: To;
  state?: unknown;
  onClick?: () => void;
  disabled?: boolean;
  describedBy?: string;
}) {
  const { condensed } = useCondensed();
  const inner = (
    <>
      {icon}
      {!condensed && label}
    </>
  );
  const common = {
    size: 'sm' as const,
    className: condensed ? 'size-8 p-0' : undefined,
    'aria-label': condensed ? label : undefined
  };
  return (
    <CondensedTip label={label}>
      {to !== undefined && !disabled ? (
        <Button asChild {...common}>
          <Link to={to} state={state}>
            {inner}
          </Link>
        </Button>
      ) : (
        <Button
          type="button"
          {...common}
          disabled={disabled}
          aria-describedby={describedBy}
          onClick={onClick}
        >
          {inner}
        </Button>
      )}
    </CondensedTip>
  );
}
